import {
  addDoc,
  collection,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { toast } from "react-toastify";

import getIsoStringFromTimestamp from "../helper/getIsoStringFromTimestamp";
import { ChatCardData, Message } from "../interfaces/Message";
import { db } from "./firebase";
import { fetchUserData } from "./user";

const createChatRoom = async (senderUid: string, receiverUid: string) => {
  const docRef = await addDoc(collection(db, "chatRooms"), {
    createdAt: Timestamp.fromDate(new Date()),
    lastMessageUpdateTime: Timestamp.fromDate(new Date()),
    users: [senderUid, receiverUid],
  });
  await setDoc(doc(db, `users/${senderUid}/chatRooms/${receiverUid}`), {
    chatRoomId: docRef.id,
  });
  await setDoc(doc(db, `users/${receiverUid}/chatRooms/${senderUid}`), {
    chatRoomId: docRef.id,
  });
  return docRef.id;
};

const getChatRoomId = async (senderUid: string, receiverUid: string) => {
  const docRef = doc(db, `users/${senderUid}/chatRooms/${receiverUid}`);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return docSnap.data().chatRoomId;
  } else {
    const chatRoomId = await createChatRoom(senderUid, receiverUid);
    return chatRoomId;
  }
};

const sendMessages = async (messageData: Message) => {
  await addDoc(collection(db, `chatRooms/${messageData.chatRoomId}/messages`), {
    ...messageData,
    createdAt: Timestamp.fromDate(new Date()),
  });
};

const fetchChatRoomIds = async (userUid: string) => {
  try {
    let queryRef = query(
      collection(db, "chatRooms"),
      where(`users`, "array-contains", userUid),
      orderBy("lastMessageUpdateTime", "desc")
    );

    const querySnapshot = await getDocs(queryRef);
    return querySnapshot.docs.map((doc) => doc.id);
  } catch (error) {
    toast.info("No previous chat present");
    return [];
  }
};

const fetchChatRooms = async (userUid: string) => {
  try {
    let queryRef = query(
      collection(db, "chatRooms"),
      where(`users`, "array-contains", userUid),
      orderBy("lastMessageUpdateTime", "desc")
    );

    const querySnapshot = await getDocs(queryRef);
    return querySnapshot.docs
      .map((doc) => ({ ...doc.data(), id: doc.id }))
      .map(adaptChatRoom);
  } catch (error) {
    toast.info("No previous chat present");
    return [];
  }
};

const fetchChatRoomById = async (chatRoomId: string) => {
  const chatRoomRef = doc(db, `chatRooms/${chatRoomId}`);
  const chatRoomSnapshot = await getDoc(chatRoomRef);
  return adaptChatRoom(chatRoomSnapshot.data());
};

const adaptChatRoom = (room: any) => {
  room.createdAt = getIsoStringFromTimestamp(room.createdAt);
  room.lastMessageUpdateTime = getIsoStringFromTimestamp(
    room.lastMessageUpdateTime
  );
  room.lastRead = room.lastRead || {};

  // Convert the last read timestamps to ISO strings
  Object.keys(room.lastRead).forEach((key) => {
    room.lastRead[key] = getIsoStringFromTimestamp(room.lastRead[key]);
  });
  return room;
};

const fetchChatCardData = async (chatRoomId: string, userUid: string) => {
  const messagesRef = collection(db, `chatRooms/${chatRoomId}/messages`);
  const latestMessageRef = query(
    messagesRef,
    orderBy("createdAt", "desc"),
    limit(1)
  );
  const messageSnapshot = await getDocs(latestMessageRef);
  const [messageData] = messageSnapshot.docs.map((doc) => {
    const result = doc.data();
    result.createdAt = result.createdAt.toDate().toISOString();
    return result;
  });
  const usersSnapshot = await getDoc(doc(db, `chatRooms/${chatRoomId}`));
  const users: string[] = usersSnapshot.get("users");
  const [receiverUid] = users.filter((uid) => uid !== userUid);
  const receiverData = await fetchUserData(receiverUid);
  const room = adaptChatRoom(usersSnapshot.data());

  const formattedData: ChatCardData = {
    messageData: messageData,
    name: receiverData && receiverData.firstName,
    receiverUid: receiverUid,
    profilePictureUrl: receiverData?.profileImage,
    room,
  };
  return formattedData;
};

const fetchMessages = async (
  chatRoomId: string,
  lastDoc: DocumentData | null
) => {
  let messagesQuery;
  if (lastDoc) {
    messagesQuery = query(
      collection(db, `chatRooms/${chatRoomId}/messages`),
      orderBy("createdAt", "desc"),
      startAfter(lastDoc),
      limit(10)
    );
  } else {
    messagesQuery = query(
      collection(db, `chatRooms/${chatRoomId}/messages`),
      orderBy("createdAt", "desc"),
      limit(10)
    );
  }

  const querySnapshot = await getDocs(messagesQuery);
  const allMessages: Message[] = await Promise.all(
    querySnapshot.docs.map(async (message) => {
      const messageData = message.data();
      messageData.createdAt =
        messageData.createdAt &&
        getIsoStringFromTimestamp(messageData.createdAt);
      return messageData as Message;
    })
  );

  const reversedMessages = allMessages.reverse();
  lastDoc === null && reversedMessages.pop();
  lastDoc = querySnapshot.docs[querySnapshot.docs.length - 1];

  return { messages: reversedMessages, lastDoc };
};

const updateChatRoomReadByUser = async (
  chatRoomId: string,
  readerUid: string
) => {
  const chatRoomRef = doc(db, `chatRooms/${chatRoomId}`);

  const lastReadField = `lastRead.${readerUid}`;

  // Update the last read timestamp for the readerUid using the field path
  await updateDoc(chatRoomRef, {
    [lastReadField]: Timestamp.fromDate(new Date()),
  });
};

/**
 * @description Check if the current user has any unread messages in the chat room
 */
const checkUnreadMessages = (userUid: string | undefined, room: any) => {
  if (
    !userUid ||
    room.lastMessageSentBy === userUid ||
    !room.lastMessageUpdateTime ||
    !room.lastRead ||
    !room.lastRead[userUid]
  ) {
    return false;
  }

  const lastMessageUpdateTime = new Date(room.lastMessageUpdateTime); // Convert to Date object

  const lastReadTimestamp = room.lastRead[userUid];
  const lastReadTime = new Date(lastReadTimestamp);

  return lastMessageUpdateTime > lastReadTime;
};

export {
  createChatRoom,
  getChatRoomId,
  sendMessages,
  fetchChatRoomIds,
  fetchChatCardData,
  fetchMessages,
  fetchChatRooms,
  updateChatRoomReadByUser,
  checkUnreadMessages,
  fetchChatRoomById,
};
